import { utils } from '@digital-commerce/shared-utils'
import { path, pipe, propOr } from 'ramda'
import { getTermInYears } from '../components/molecules/CartItem/utils'
import {
  getContractLength,
  getHSCredits,
  getHSPromoCode,
  getManuallyAppliedPromoCode,
  isCodeRedemptionEnabled
} from './cart'
import { formatCurrency } from './currency'
import { getDiscountDisplayName } from './discount'
import {
  getBIBAmount,
  getConnectionFee,
  getConnectionFeeValues,
  getItemRecurringDiscounts,
  getOneTimeItemDiscounts,
  getPendingBalanceFromOffer,
  getTerm,
  getVASAddOns,
  isSmartwatch,
  productDetailsFactory
} from './mobility'

const { hasContractTerm, getDisplayName, getMonthlyItemPrice } = utils.cart

/**
 * @typedef {object} mappedDisplayItem
 * @property {string} id Id of item
 * @property {string} name Name of item
 * @property {string} price Price of item
 * @property {string} prefixIcon Icon to be prefixed before the name
 * See available icon options on TDS or Allium
 */

/**
 * Takes a top level item containing contract information and returns the term
 * in years if applicable.
 * @param {Function} translate translate function
 * @param {object} service service item
 * @param {object} [replacementsOverride] optional replacementOverride
 * @returns {string|null} The contract term for the service.
 * 'No term' if no contract term exists.
 * Or null if service is null
 */
export const buildContractTermLabel = (translate, service, replacementsOverride) => {
  if (!service) return null

  const termLabelTranslateOptions = {
    replacements: {
      numberOfYear: getTermInYears(service),
      ...replacementsOverride
    }
  }

  if (hasContractTerm(service)) {
    const contractTerm = getContractLength(service)
    return contractTerm % 12 === 0
      ? translate('termLabel', termLabelTranslateOptions)
      : translate('monthsTermLabel', termLabelTranslateOptions)
  }

  return translate('noTermLabel')
}

/**
 * Retrieve display text from HS promo code item
 * @param {object} service Top-level service item
 * @param {object} featureFlags Feature flags response from Contentful
 * @param {object} cart Cart response
 * @returns {string} Display text from promo code item
 */
export const getHSAppliedPromoCodeText = (service, featureFlags, cart) => {
  const isPromocodeEnabled = isCodeRedemptionEnabled(featureFlags)
  return isPromocodeEnabled ? getManuallyAppliedPromoCode(service, getHSPromoCode(cart)) : null
}

/**
 * Builds mapping to display all HS credits related to the provided service.
 * To be used within the HeadingWithSubitems or NameAndPrice molecules.
 * @param {string} serviceTag Service item tag
 * @param {object} cart Cart response
 * @param {string} lang Value from Locale Context
 * @returns {[mappedDisplayItem]} Array of objects mapped to be displayed
 */
export const buildHSCreditList = (serviceTag, cart, lang) => {
  const credits = getHSCredits(serviceTag)(cart)
  const price = pipe(path(['totalPrice', 'recurring', 0, 'recurringCreditBreakdowns', 0, 'credit']))

  return credits?.map((item) => ({
    id: item.id,
    name: getDisplayName(item),
    price: `-${formatCurrency(price(item), lang)}`,
    prefixIcon: 'DEALS'
  }))
}

/**
 * Formats a list of addons to customer friendly text
 * Examples: Addon: (Addon1) | Add-ons: (Addon1), (Addon2), (Addon3)
 * @param {array} addons list of addons
 * @param {function} translate translate function
 * @returns {string} Result string
 */
export const buildAddonItemsText = (addons, translate) => {
  const addonLabel = addons.length <= 1 ? translate('addon') : translate('addons')

  return addons.reduce((acc, cur, index) => {
    if (index === 0) return `${acc}: ${getDisplayName(cur)}`
    return `${acc}, ${getDisplayName(cur)}`
  }, addonLabel)
}

export const getStorageAttributeText = (device, translate) => {
  const topLevelItem = productDetailsFactory(device)
  const { storage } = topLevelItem

  const caseSizeText = translate('item.caseSize', { replacements: { storage } })
  const memorySizeText = translate('item.size', { replacements: { storage } })

  return storage !== 'none' && typeof storage !== 'undefined'
    ? `${isSmartwatch(device) ? caseSizeText : memorySizeText}`
    : ''
}

export const getColourAttributeText = (device, translate) => {
  const topLevelItem = productDetailsFactory(device)
  const { colour } = topLevelItem

  return `${translate('item.colour')}: ${colour}`
}

export const buildMobilityDeviceOneTimeDiscountList = (device, lang) => {
  const discounts = getOneTimeItemDiscounts(device)

  return discounts.map((discount, idx) => ({
    id: `${device.id}-discount-${idx}`,
    name: getDiscountDisplayName(discount),
    price: `-${formatCurrency(discount.amount, lang)}`,
    prefixIcon: 'DEALS'
  }))
}

/**
 * Builds the monthly price for the telus easy payment of a mobility device.
 * This function can either use the recurringDuration value of the plan
 * when a plan item is passed as a parameter.
 * Otherwise it will use the recurringDuration value from the device item
 * @param {object} device mobility device item
 * @param {object} plan mobility plan item (MOBILITY_PLAN)
 * @param {function} translate translate function
 * @param {string} lang lang locale
 * @returns {object|null} Object containing easy payment info,
 * or null when there is no monthlyPrice or recurringDuration
 * */
export const buildMobilityMonthlyEasyPaymentPrice = ({ device, plan, translate, lang }) => {
  const { amount: monthlyPrice, duration } = getMonthlyItemPrice(device)

  if (!monthlyPrice || monthlyPrice <= 0) return null

  const durationLabel = plan ? getTerm(plan) : duration

  if (!durationLabel) return null

  return {
    name: translate('item.easyPayment', { replacements: { duration: durationLabel } }),
    price: `${formatCurrency(monthlyPrice, lang)}/mo.`,
    id: device.id
  }
}

/**
 * Builds the monthly price of a mobility plan item
 * @param {object} plan mobility plan item (MOBILITY_PLAN)
 * @param {string} lang lang locale
 * @returns {object} Object containing plan price info
 * */
export const buildMobilityPlanPrice = (plan, lang) => {
  return {
    id: `${plan.id}-due-monthly-plan`,
    name: getDisplayName(plan),
    price: `${formatCurrency(getMonthlyItemPrice(plan).amount, lang)}/mo.`
  }
}

/**
 * Builds the connection fee details of a mobility offer.
 * Either displays the connection fee price when it applies (price is > 0), or the waived price
 * @param {object} offer mobility offer item (MOBILITY_OFFER) containing a conneciton fee item
 * @param {function} translate translate function
 * @param {string} lang lang locale
 * @returns {object} Object containing connection fee info
 * */
export const buildConnectionFeeData = (offer, translate, lang) => {
  const connectionFee = getConnectionFee(offer)
  const { actual, reference } = getConnectionFeeValues(connectionFee)

  const isConnectionFeeApplied = actual > 0

  const label = `item.${isConnectionFeeApplied ? 'connectionFeeApplied' : 'connectionFeeWaived'}`
  const connectionFeeValue = isConnectionFeeApplied ? actual : reference
  return {
    id: offer.id,
    name: translate(label, { replacements: { connectionFeeValue } }),
    price: formatCurrency(actual, lang)
  }
}

/**
 * Builds a list of VAS addons to be displayed
 * @param {object} offer mobility offer item (MOBILITY_OFFER)
 * @param {object} plan mobility plan item. Used to determine term duration,
 * defaults to 24 months when this param is null. (Example: BYOD)
 * @param {function} translate translate function
 * @param {string} lang lang locale
 * @returns {Array} List containing VAS addons and any VAS discounts if applicable
 * */
export const buildVasAddOnsList = ({ offer, plan, translate, lang }) => {
  const DEFAULT_TERM = 24
  const addonsBreakdownList = []

  const vasAddons = getVASAddOns(offer)
  const term = plan ? getTerm(plan) : DEFAULT_TERM

  vasAddons.forEach((addon) => {
    addonsBreakdownList.push({
      id: `mobility-vas-breakdown-${addon.id}`,
      name: getDisplayName(addon),
      price: `${formatCurrency(addon.itemPrice.recurring[0].amount, lang)}/mo.`
    })

    // Gets recurring discounts for each vas addon
    const recurringDiscounts = getItemRecurringDiscounts(addon, term)
    recurringDiscounts?.forEach((discount, idx) => {
      const numberOfYear = propOr(DEFAULT_TERM, 'recurringDuration', discount) / 12
      const nameLabel = `${translate('priceBreakdown.discountLabel', {
        replacements: { numberOfYear }
      })}`

      addonsBreakdownList.push({
        id: `mobility-vas-breakdown-${addon.id}-discount-${idx}`,
        name: nameLabel,
        price: `-${formatCurrency(discount.amount, lang)}/mo.`,
        prefixIcon: 'DEALS'
      })
    })
  })

  return addonsBreakdownList
}

export const buildOrbitPendingBalancesList = (offer, lang, translate) => {
  const pendingBalances = getPendingBalanceFromOffer(offer)
  if (!pendingBalances?.individual?.length || (!pendingBalances?.total && !pendingBalances?.hasPenalty)) return []

  return pendingBalances?.individual?.reduce((acc, balance) => {
    // add penalty line item
    acc.push({
      id: `${offer.id}-${balance.name}`,
      name: translate(`orbit.pendingAmounts.${balance.name}`),
      price: formatCurrency(balance.amount, lang)
    })

    // add corresponding penalty alterations
    balance.alterations?.forEach(alteration => {
      acc.push({
        id: `${offer.id}-${alteration.name}-${alteration.amount}`,
        name: translate('orbit.pendingAmounts.balanceWaived'),
        price: formatCurrency(-balance.amount, lang)
      })
    })

    return acc
  }, [])
}

export const buildOrbitBIBAmount = (device, translate, lang) => {
  const bibAmount = getBIBAmount(device)

  if (bibAmount <= 0) return undefined

  return {
    id: `${device.id}-bib-savings`,
    name: translate('orbit.bringItBackSavings'),
    price: `-${formatCurrency(bibAmount, lang)}`
  }
}
